import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LoanShell } from '../components/loan/LoanShell';
import { TaskLayout } from '../components/tasks/TaskLayout';
import { TaskSuccess } from '../components/tasks/TaskSuccess';
import { AppraisalBody, DocumentRequestBody, QuestionBody, TitleBody } from '../components/tasks/TaskBodies';
import { usePortal } from '../contexts/PortalContext';
import { useTokens } from '../hooks/useTokens';
import type { TaskKind } from '../types/application';

// Copy supports {{tokens}} — see utils/template.ts.
const copy: Record<TaskKind, {
  heading: string;
  why: string;
  after: string;
  success: [string, string];
}> = {
  document: {
    heading: '{{processorFirstName}} needs one more document',
    why: 'The underwriter needs your most recent month to confirm your down payment is still in your account.',
    after: '{{processorFirstName}} reviews it within 1 business day. If it looks good, this item clears automatically.',
    success: ['Sent to {{processorFirstName}}', 'It’ll be reviewed within 1 business day. We’ll text you when it’s accepted.']
  },
  question: {
    heading: 'A quick question about a deposit',
    why: 'Lenders confirm that large deposits aren’t borrowed money. It’s routine — almost everyone gets one of these.',
    after: '{{processorFirstName}} adds your answer to your file and may ask for one supporting document if needed.',
    success: ['Thanks, that helps', '{{processorFirstName}} will let you know if anything else is needed.']
  },
  appraisal: {
    heading: 'Schedule property access',
    why: 'An independent appraiser needs about 30 minutes inside the home to confirm its value.',
    after: 'We confirm the time with the appraiser and your agent. You’ll see the result here in about a week.',
    success: ['Appraisal visit scheduled', 'We’ll send a reminder the day before. Your results will appear here.']
  },
  title: {
    heading: 'How would you like to take title?',
    why: 'This decides whose names go on the deed to your new home.',
    after: 'The title company prepares your closing documents with these names. You’ll review them before signing.',
    success: ['Title choice saved', 'The title company will use this for your closing documents.']
  }
};
export function TaskDetail() {
  const {
    taskId
  } = useParams();
  const {
    tasks,
    completeTask
  } = usePortal();
  const [done, setDone] = useState(false);
  const {
    fill
  } = useTokens();
  useEffect(() => setDone(false), [taskId]);
  const task = tasks.find(t => t.id === taskId);
  if (!task) return <Navigate to="/loan" replace data-id="element-1079" data-mp="1g0i2ray73pkl" />;
  const c = copy[task.kind];
  const finish = () => {
    completeTask(task.id);
    setDone(true);
  };
  return <LoanShell data-id="element-1080" data-mp="2rgyi749r267g">
      <AnimatePresence mode="wait" data-id="element-1081" data-mp="2rgezvmg6tfsw">
        {done ? <motion.div key="done" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.2
      }} data-id="element-1082" data-mp="2mix1gpzsqiz5">
            <TaskSuccess title={fill(c.success[0])} body={fill(c.success[1])} data-id="element-1083" data-mp="2u1wkuvn9caii" />
          </motion.div> : <motion.div key="task" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-1084" data-mp="2z2alvtqfyk71">
            <TaskLayout task={task} heading={fill(c.heading)} why={fill(c.why)} after={fill(c.after)} data-id="element-1085" data-mp="2p2balp2n0fqh">
              {task.kind === 'document' && <DocumentRequestBody onComplete={finish} data-id="element-1086" data-mp="3ct79151lm96p" />}
              {task.kind === 'question' && <QuestionBody onComplete={finish} data-id="element-1087" data-mp="3hqfj0iclf6wv" />}
              {task.kind === 'appraisal' && <AppraisalBody onComplete={finish} data-id="element-1088" data-mp="3415ajcb3b2z2" />}
              {task.kind === 'title' && <TitleBody onComplete={finish} data-id="element-1089" data-mp="381p8uj7egcms" />}
            </TaskLayout>
          </motion.div>}
      </AnimatePresence>
    </LoanShell>;
}